/**
 * Detalle del paciente. MVP: info bio + clínica básica.
 *
 * En siguientes iteraciones: historia clínica, citas, recetas, adjuntos.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Repeat,
  Calendar,
  CalendarPlus,
  Droplets,
  Edit2,
  FileSignature,
  Mail,
  MapPin,
  Phone,
  Plus,
  Stethoscope,
  User,
  CornerDownRight,
} from "lucide-react";
import { type EpisodioListItem, getHistoriaDePaciente } from "../api/clinical";
import {
  type GrupoSanguineo,
  type PacienteUpdate,
  type Sexo,
  getPaciente,
  updatePaciente,
} from "../api/pacientes";
import {
  type Cita,
  type OrigenClinico,
  cancelarCita,
  listCitas,
} from "../api/citas";
import { useAuth } from "../auth/AuthProvider";
import { useBase, useZonaHoraria } from "../auth/TenantBrandingProvider";
import { formatApiError } from "../api/client";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
  Field,
  Input,
} from "../components/ui";
import { AgendarCitaDialog } from "../components/atencion/AgendarCitaDialog";
import { AccesoPortalCard } from "../components/pacientes/AccesoPortalCard";
import { ApoderadosCard } from "../components/pacientes/ApoderadosCard";
import { EstadoFlujoDeCita } from "../components/atencion/EstadoFlujo";
import { PlanCard } from "../components/atencion/PlanTratamiento";
import { listPlanes } from "../api/planes";
import { TelefonoInput, formatearTelefono } from "../components/TelefonoInput";
import { fechaDeInstante, fechaHora, fechaSimple } from "../lib/fecha";
import { FECHA_MINIMA, edadLegible, hoyISO } from "../lib/edad";

const GRUPOS_SANGRE: GrupoSanguineo[] = [
  "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-",
];

const MEDIO_LABEL: Record<string, string> = {
  FISICO: "Firmó el formato",
  VERBAL: "Verbal ante el personal",
  DIGITAL: "Firma digital",
};

export function PacienteDetailPage() {
  const tz = useZonaHoraria();
  const { id } = useParams<{ id: string }>();
  const base = useBase();
  const pacienteId = id ? Number(id) : NaN;
  const [editOpen, setEditOpen] = useState(false);
  const { me } = useAuth();
  const puedeEditar =
    me?.is_super_admin || (me?.permisos.includes("paciente:update") ?? false);

  const { data: paciente, isLoading, error } = useQuery({
    queryKey: ["paciente", pacienteId],
    queryFn: () => getPaciente(pacienteId),
    enabled: Number.isFinite(pacienteId),
  });

  if (!Number.isFinite(pacienteId)) {
    return <Navigate to={`${base}/pacientes`} replace />;
  }

  // A quién se llama por este paciente. En un menor es su apoderado.
  const principal =
    paciente?.apoderados.find((a) => a.es_principal) ?? paciente?.apoderados[0];

  return (
    <div className="space-y-6">
      <Link
        to={`${base}/pacientes`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Todos los pacientes
      </Link>

      {isLoading && <div className="text-sm text-slate-400">Cargando…</div>}
      {error && <Alert>No se pudo cargar el paciente.</Alert>}

      {paciente && (
        <>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)] flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {paciente.nombre_completo}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  {paciente.num_documento ? (
                    <span className="font-mono">
                      {paciente.tipo_documento} {paciente.num_documento}
                    </span>
                  ) : (
                    // Se ve, y se puede arreglar desde Editar. Un pendiente
                    // que no se nota es un pendiente que nadie regulariza.
                    <Badge variant="warning">Documento por regularizar</Badge>
                  )}
                  {paciente.fecha_nacimiento && (
                    <>
                      {" "}· {edadEnAnios(paciente.fecha_nacimiento)} años (
                      {fechaSimple(paciente.fecha_nacimiento)})
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={paciente.activo ? "success" : "danger"}>
                {paciente.activo ? "Activo" : "Inactivo"}
              </Badge>
              <Button variant="ghost" onClick={() => setEditOpen(true)}>
                <Edit2 className="w-4 h-4" />
                Editar
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Demográficos</h2>
              </CardHeader>
              <CardBody className="space-y-2 text-sm">
                <Row
                  icon={User}
                  label="Sexo"
                  value={
                    paciente.sexo === "M"
                      ? "Masculino"
                      : paciente.sexo === "F"
                        ? "Femenino"
                        : paciente.sexo === "O"
                          ? "Otro"
                          : "—"
                  }
                />
                <Row
                  icon={Calendar}
                  label="Nacimiento"
                  value={
                    paciente.fecha_nacimiento
                      ? fechaSimple(paciente.fecha_nacimiento)
                      : "—"
                  }
                />
                <Row
                  icon={Droplets}
                  label="Grupo sanguíneo"
                  value={paciente.grupo_sanguineo ?? "—"}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Contacto</h2>
              </CardHeader>
              <CardBody className="space-y-2 text-sm">
                {/* En un menor el contacto es el de su apoderado: pedirle
                    teléfono propio a un bebé es pedir un dato que no existe. */}
                {paciente.es_menor && !paciente.telefono ? (
                  <Row
                    icon={Phone}
                    label="Teléfono (apoderado)"
                    value={
                      principal
                        ? `${formatearTelefono(principal.telefono)} · ${principal.nombre_completo}`
                        : "Sin apoderado registrado"
                    }
                  />
                ) : (
                  <Row
                    icon={Phone}
                    label="Teléfono"
                    value={formatearTelefono(paciente.telefono)}
                  />
                )}
                <Row
                  icon={Mail}
                  label="Email"
                  value={paciente.email ?? principal?.email ?? "—"}
                />
                <Row
                  icon={MapPin}
                  label="Dirección"
                  value={
                    paciente.direccion
                      ? `${paciente.direccion}${paciente.ubigeo ? ` (${paciente.ubigeo})` : ""}`
                      : "—"
                  }
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Cumplimiento</h2>
              </CardHeader>
              <CardBody className="space-y-2 text-sm">
                <Row
                  icon={User}
                  label="Consentimiento Ley 29733"
                  value={
                    paciente.consentimiento_lpdp
                      ? `${MEDIO_LABEL[paciente.consentimiento_medio ?? ""] ?? "Registrado"} · ${
                          paciente.consentimiento_fecha
                            ? fechaDeInstante(paciente.consentimiento_fecha, tz)
                            : "—"
                        }`
                      : "Pendiente de regularizar"
                  }
                />
                <Row
                  icon={Calendar}
                  label="Registro"
                  value={fechaDeInstante(paciente.created_at, tz)}
                />
              </CardBody>
            </Card>
          </div>

          <ApoderadosCard paciente={paciente} puedeEditar={puedeEditar} />

          {/* El acceso al portal se da desde aquí porque es donde admisión
              tiene al paciente delante y su documento a la vista. */}
          <AccesoPortalCard paciente={paciente} puedeEditar={puedeEditar} />

          <PlanesDelPaciente pacienteId={paciente.id} />

          {/* Aquí no va la guía del recorrido. A esta ficha se entra a LEER la
              historia de alguien, no a aprenderse el orden del circuito, y un
              diagrama de cinco pasos entre los datos del paciente y sus
              atenciones solo separa lo que se viene a leer. */}

          {/* El triaje y la derivación ya no viven sueltos aquí: pertenecen a
              una atención concreta y se hacen dentro de ella. Entra a la cita. */}
          <AtencionesTimeline pacienteId={paciente.id} />

          <EditPacienteDialog
            paciente={paciente}
            open={editOpen}
            onClose={() => setEditOpen(false)}
          />
        </>
      )}
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}

/** Guía visual del flujo clínico correcto (según NTS 139-MINSA). */
/**
 * Los tratamientos del paciente, con su progreso. Es donde admisión llega
 * desde la bandeja para darles hora a las sesiones que faltan.
 */
function PlanesDelPaciente({ pacienteId }: { pacienteId: number }) {
  const { me } = useAuth();
  // Esta ficha es historia clínica: el médico viene a leer atenciones, no
  // horarios. Las sesiones con sus fechas son trabajo de agenda, y verlas aquí
  // —encima las de un tratamiento que lleva otro profesional— solo confunde.
  // Quien agenda para la clínica sí las necesita: llega desde su bandeja.
  const agendaParaTodos =
    me?.is_super_admin || (me?.permisos.includes("profesional:read_all") ?? false);

  const { data: planes = [] } = useQuery({
    queryKey: ["planes", "paciente", pacienteId],
    queryFn: () => listPlanes({ paciente_id: pacienteId }),
    enabled: agendaParaTodos,
  });
  if (!agendaParaTodos || planes.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <h2 className="flex items-center gap-2 font-semibold text-slate-900">
          <Repeat className="h-4 w-4 text-[color:var(--color-link)]" />
          Tratamientos
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Sesiones indicadas en consulta. Cada una se cobra y no repite triaje.
        </p>
      </CardHeader>
      <CardBody className="space-y-3">
        {planes.map((p) => (
          <PlanCard key={p.id} plan={p} />
        ))}
      </CardBody>
    </Card>
  );
}


function edadEnAnios(fecha: string): number {
  const nacimiento = new Date(fecha);
  const hoy = new Date();
  let anios = hoy.getFullYear() - nacimiento.getFullYear();
  const m = hoy.getMonth() - nacimiento.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
    anios--;
  }
  return anios;
}

function EditPacienteDialog({
  paciente,
  open,
  onClose,
}: {
  paciente: any;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PacienteUpdate>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (paciente) {
      setForm({
        nombres: paciente.nombres,
        ap_paterno: paciente.ap_paterno,
        ap_materno: paciente.ap_materno ?? "",
        fecha_nacimiento: paciente.fecha_nacimiento ?? null,
        sexo: paciente.sexo,
        email: paciente.email ?? "",
        telefono: paciente.telefono ?? "",
        ubigeo: paciente.ubigeo ?? "",
        direccion: paciente.direccion ?? "",
        grupo_sanguineo: paciente.grupo_sanguineo,
        activo: paciente.activo,
      });
      setError(null);
    }
  }, [paciente]);

  const mutation = useMutation({
    mutationFn: (payload: PacienteUpdate) =>
      updatePaciente(paciente.id, {
        ...payload,
        // Vacío significa "sigue pendiente", no "bórralo".
        num_documento: payload.num_documento || undefined,
        tipo_documento: payload.num_documento ? payload.tipo_documento : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["paciente", paciente.id] });
      queryClient.invalidateQueries({ queryKey: ["pacientes"] });
      onClose();
    },
    onError: (err) => {
      setError(formatApiError(err, "No se pudo guardar."));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      ...form,
      ap_materno: form.ap_materno || null,
      email: form.email || null,
      telefono: form.telefono || null,
      ubigeo: form.ubigeo || null,
      direccion: form.direccion || null,
    });
  }

  return (
    <Dialog open={open} onClose={onClose} title="Editar paciente" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {/* Regularizar el documento. Solo aparece si falta: un documento ya
            registrado no se reescribe desde acá — eso convertiría una ficha en
            la de otra persona. */}
        {!paciente?.num_documento && (
          <div className="grid grid-cols-3 gap-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
            <Field label="Tipo de documento" htmlFor="e_tipodoc">
              <select
                id="e_tipodoc"
                value={form.tipo_documento ?? paciente?.tipo_documento ?? "DNI"}
                onChange={(e) =>
                  setForm({ ...form, tipo_documento: e.target.value as any })
                }
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="DNI">DNI</option>
                <option value="CNV">Certificado de nacido vivo</option>
                <option value="CE">Carnet de extranjería</option>
                <option value="PAS">Pasaporte</option>
              </select>
            </Field>
            <Field
              label="Número"
              htmlFor="e_numdoc"
              hint="Regulariza el documento que quedó pendiente."
            >
              <Input
                id="e_numdoc"
                value={form.num_documento ?? ""}
                onChange={(e) =>
                  setForm({ ...form, num_documento: e.target.value.toUpperCase() })
                }
                className="font-mono"
                placeholder="Aún pendiente"
              />
            </Field>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Nombres" htmlFor="e_nombres">
            <Input
              id="e_nombres"
              value={form.nombres ?? ""}
              onChange={(e) => setForm({ ...form, nombres: e.target.value })}
              required
            />
          </Field>
          <Field label="Ap. paterno" htmlFor="e_apat">
            <Input
              id="e_apat"
              value={form.ap_paterno ?? ""}
              onChange={(e) => setForm({ ...form, ap_paterno: e.target.value })}
              required
            />
          </Field>
          <Field label="Ap. materno" htmlFor="e_amat">
            <Input
              id="e_amat"
              value={form.ap_materno ?? ""}
              onChange={(e) => setForm({ ...form, ap_materno: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Nacimiento"
            htmlFor="e_fnac"
            hint={edadLegible(form.fecha_nacimiento) ?? undefined}
          >
            <Input
              id="e_fnac"
              type="date"
              value={form.fecha_nacimiento ?? ""}
              max={hoyISO()}
              min={FECHA_MINIMA}
              onChange={(e) =>
                setForm({ ...form, fecha_nacimiento: e.target.value || null })
              }
            />
          </Field>
          <Field label="Sexo" htmlFor="e_sexo">
            <select
              id="e_sexo"
              value={form.sexo ?? ""}
              onChange={(e) =>
                setForm({ ...form, sexo: (e.target.value || null) as Sexo | null })
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">—</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="O">Otro</option>
            </select>
          </Field>
          <Field label="Grupo sanguíneo" htmlFor="e_gs">
            <select
              id="e_gs"
              value={form.grupo_sanguineo ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  grupo_sanguineo: (e.target.value || null) as GrupoSanguineo | null,
                })
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">—</option>
              {GRUPOS_SANGRE.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Teléfono" htmlFor="e_tel">
            <TelefonoInput
              id="e_tel"
              value={form.telefono}
              onChange={(v) => setForm({ ...form, telefono: v })}
            />
          </Field>
          <Field label="Email" htmlFor="e_email">
            <Input
              id="e_email"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-[140px_1fr] gap-3">
          <Field label="Ubigeo" htmlFor="e_ubigeo">
            <Input
              id="e_ubigeo"
              value={form.ubigeo ?? ""}
              onChange={(e) => setForm({ ...form, ubigeo: e.target.value })}
              maxLength={6}
              className="font-mono"
            />
          </Field>
          <Field label="Dirección" htmlFor="e_dir">
            <Input
              id="e_dir"
              value={form.direccion ?? ""}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })}
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.activo ?? true}
            onChange={(e) => setForm({ ...form, activo: e.target.checked })}
            className="rounded border-slate-300 text-[color:var(--color-link)] focus:ring-[color:var(--color-link)]"
          />
          Paciente activo
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Guardar cambios
          </Button>
        </div>
      </form>
    </Dialog>
  );
}


// ---------- Citas del paciente ----------


// ---------- Línea de tiempo unificada de atenciones ----------

type TimelineItem =
  | { kind: "cita"; fecha: string; cita: Cita; ep: EpisodioListItem | undefined }
  | { kind: "episodio"; fecha: string; ep: EpisodioListItem };

function LineaOrigen({ origen }: { origen: OrigenClinico }) {
  const base = useBase();
  const tz = useZonaHoraria();
  const destino =
    origen.episodio_id != null
      ? `${base}/episodios/${origen.episodio_id}`
      : origen.cita_id != null
        ? `${base}/citas/${origen.cita_id}`
        : null;

  const texto =
    origen.tipo === "CONTROL"
      ? `Control ${origen.numero_sesion ?? "?"} de ${origen.total_sesiones ?? "?"}`
      : "Derivación";

  return (
    <p className="mb-1 flex flex-wrap items-center gap-1 text-xs text-slate-500">
      <CornerDownRight className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="font-medium text-slate-600">{texto}</span>
      <span>· sale de la atención</span>
      {origen.especialidad && <span>de {origen.especialidad}</span>}
      {origen.fecha && <span>del {fechaHora(origen.fecha, tz)}</span>}
      {destino && (
        <Link
          to={destino}
          className="font-medium text-[color:var(--color-link)] hover:underline"
        >
          ver
        </Link>
      )}
    </p>
  );
}

function DxLine({ ep }: { ep: EpisodioListItem }) {
  if (!ep.dx_principal) {
    return <p className="text-xs text-slate-400">Sin diagnóstico registrado.</p>;
  }
  return (
    <p className="text-xs text-slate-600">
      Dx: <span className="font-mono text-slate-500">{ep.dx_principal_codigo}</span>{" "}
      {ep.dx_principal}
    </p>
  );
}

/**
 * Historial cronológico unificado del paciente: fusiona citas y atenciones
 * (ya enlazadas) en una sola línea de tiempo. Cada entrada muestra el estado
 * del proceso (agendada → pagada → atendida) y su diagnóstico.
 */
function AtencionesTimeline({ pacienteId }: { pacienteId: number }) {
  const queryClient = useQueryClient();
  const tz = useZonaHoraria();
  const { me } = useAuth();
  const [agendarOpen, setAgendarOpen] = useState(false);
  // Quien atiende suele venir a repasar LO SUYO —"¿qué le puse la última vez
  // que lo vi?"— y en un paciente que rota por cinco especialidades sus
  // consultas quedan enterradas entre las de los demás. El historial completo
  // sigue estando: es un filtro, no un recorte de lo que puede ver.
  const miProfesionalId = me?.profesional_id ?? null;
  const [soloMias, setSoloMias] = useState(false);
  // Agenda quien agenda para toda la clínica: admisión y admin. El criterio es
  // `profesional:read_all` —el mismo que gobierna ver la agenda ajena— porque
  // para dar una hora hay que poder mirar la agenda del profesional al que se
  // la das. El médico no lo tiene: él deriva o indica sesiones desde su propia
  // atención.
  const puedeAgendarCitas =
    me?.is_super_admin || (me?.permisos.includes("profesional:read_all") ?? false);

  const { data: citas = [], isLoading } = useQuery({
    queryKey: ["citas", "paciente", pacienteId],
    queryFn: () => listCitas({ paciente_id: pacienteId, incluir_canceladas: true }),
  });
  const { data: historia } = useQuery({
    queryKey: ["historia", pacienteId],
    queryFn: () => getHistoriaDePaciente(pacienteId),
  });

  // Cancelar una cita libera la hora y obliga al paciente a volver a pedirla:
  // no puede pasar por un clic de más en un enlace rojo.
  const [porCancelar, setPorCancelar] = useState<Cita | null>(null);
  const cancelar = useMutation({
    mutationFn: (id: number) => cancelarCita(id),
    onSuccess: () => {
      setPorCancelar(null);
      queryClient.invalidateQueries({ queryKey: ["citas", "paciente", pacienteId] });
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
    },
  });

  const episodioById = new Map<number, EpisodioListItem>();
  (historia?.episodios ?? []).forEach((e) => episodioById.set(e.id, e));
  const linkedEpIds = new Set(
    citas.map((c) => c.episodio_id).filter((x): x is number => x != null),
  );

  const todos: TimelineItem[] = [
    ...citas.map(
      (c): TimelineItem => ({
        kind: "cita",
        fecha: c.inicio,
        cita: c,
        ep: c.episodio_id != null ? episodioById.get(c.episodio_id) : undefined,
      }),
    ),
    ...(historia?.episodios ?? [])
      .filter((e) => !linkedEpIds.has(e.id))
      .map((e): TimelineItem => ({ kind: "episodio", fecha: e.inicio, ep: e })),
  ].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

  const esMio = (it: TimelineItem) =>
    it.kind === "cita"
      ? it.cita.profesional_id === miProfesionalId
      : it.ep.profesional_id === miProfesionalId;
  const mias = miProfesionalId != null ? todos.filter(esMio) : [];
  const items = soloMias ? mias : todos;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-[color:var(--color-link)]" />
              Atenciones
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Historial cronológico: cada cita, su pago y la consulta con su diagnóstico.
              {historia && (
                <>
                  {" · HC "}
                  <span className="font-mono">{historia.numero}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {miProfesionalId != null && (
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-xs">
                {[
                  { id: false, label: `Todas (${todos.length})` },
                  { id: true, label: `Mías (${mias.length})` },
                ].map((op) => (
                  <button
                    key={String(op.id)}
                    type="button"
                    onClick={() => setSoloMias(op.id)}
                    className={`rounded-md px-3 py-1.5 font-medium transition ${
                      soloMias === op.id
                        ? "bg-[color:var(--color-link)] text-white"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            )}
            {/* Agendar es de admisión: este diálogo deja elegir especialidad,
                sede y profesional cualesquiera, y un médico no da horas en la
                agenda de otro ni abre citas de otra especialidad. Lo suyo es
                derivar o indicar sesiones desde su propia atención. */}
            {puedeAgendarCitas && (
              <Button onClick={() => setAgendarOpen(true)}>
                <CalendarPlus className="w-4 h-4" />
                Agendar cita
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {isLoading && (
          <div className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-slate-500">
            {soloMias ? (
              <>
                <p>Todavía no has atendido a este paciente.</p>
                <button
                  type="button"
                  onClick={() => setSoloMias(false)}
                  className="mt-2 font-medium text-[color:var(--color-link)] hover:underline"
                >
                  Ver todas sus atenciones →
                </button>
              </>
            ) : (
              "Sin atenciones aún. Agenda la primera cita para iniciar el proceso."
            )}
          </div>
        )}
        {items.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {items.map((it) => (
              <TimelineEntry
                key={it.kind === "cita" ? `c${it.cita.id}` : `e${it.ep.id}`}
                item={it}
                onCancel={() => it.kind === "cita" && setPorCancelar(it.cita)}
              />
            ))}
          </ul>
        )}
      </CardBody>
      <AgendarCitaDialog
        pacienteId={pacienteId}
        open={agendarOpen}
        onClose={() => setAgendarOpen(false)}
      />

      <ConfirmDialog
        open={porCancelar !== null}
        onClose={() => setPorCancelar(null)}
        onConfirm={() => porCancelar && cancelar.mutate(porCancelar.id)}
        loading={cancelar.isPending}
        title="Cancelar cita"
        description="La hora queda libre para otro paciente y este tendrá que pedir una nueva. La cita no se borra: queda en el historial como cancelada."
        detalle={
          porCancelar && (
            <>
              <span className="font-medium">{fechaHora(porCancelar.inicio, tz)}</span>
              <br />
              {porCancelar.especialidad_nombre} · {porCancelar.profesional_nombre}
              {porCancelar.pago_estado === "PAGADO" && (
                <span className="mt-1 block text-amber-700">
                  Ya está cobrada: la devolución se resuelve en caja.
                </span>
              )}
            </>
          )
        }
        confirmLabel="Sí, cancelar la cita"
        cancelLabel="No, dejarla"
      />
    </Card>
  );
}

function TimelineEntry({
  item,
  onCancel,
}: {
  item: TimelineItem;
  onCancel: () => void;
}) {
  const base = useBase();
  const tz = useZonaHoraria();
  const { me } = useAuth();
  const puedeTriar =
    me?.is_super_admin || (me?.permisos.includes("triaje:create") ?? false);
  const fecha = fechaHora(item.fecha, tz);
  const linkCls = "text-xs text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium";

  if (item.kind === "episodio") {
    const ep = item.ep;
    return (
      <li className="px-6 py-3">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <Badge variant="success">Atención directa</Badge>
          <span className="text-sm font-medium text-slate-900">
            {ep.especialidad_nombre ?? "—"}
          </span>
          <span className="text-xs text-slate-500">· {ep.profesional_nombre}</span>
          <span className="text-xs text-slate-400 ml-auto">{fecha}</span>
        </div>
        <DxLine ep={ep} />
        <Link to={`${base}/episodios/${ep.id}`} className={linkCls}>
          Ver atención →
        </Link>
      </li>
    );
  }

  const c = item.cita;
  const ep = item.ep;
  const cerrada = c.estado === "CANCELADA" || c.estado === "COMPLETADA";

  return (
    <li className="px-6 py-3">
      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
        <EstadoFlujoDeCita cita={c} conPasos />
        <Link
          to={`${base}/citas/${c.id}`}
          className="text-sm font-medium text-slate-900 hover:text-[color:var(--color-link)] hover:underline"
        >
          {c.especialidad_nombre}
        </Link>
        <span className="text-xs text-slate-500">
          · {c.profesional_nombre} · {c.sede_nombre}
        </span>
        <span className="text-xs text-slate-400 ml-auto">{fecha}</span>
      </div>
      {/* De dónde salió esta cita.

          Sin esto, el historial es una lista de citas sueltas: con tres
          visitas de Ginecología y una de Odontología no hay forma de saber
          cuál nació de cuál, y los dos controles de una consulta se leen igual
          que una cita que el paciente pidió por su cuenta. */}
      {c.origen_clinico && <LineaOrigen origen={c.origen_clinico} />}
      {ep && (
        <div className="mb-1">
          <DxLine ep={ep} />
        </div>
      )}
      <div className="flex items-center gap-3 mt-1">
        {/* Todo lo de la atención (triaje, pago, consulta, derivación) se hace
            dentro de ella. Aquí solo quedan los atajos más usados. */}
        <Link to={`${base}/citas/${c.id}`} className={linkCls}>
          Abrir atención →
        </Link>
        {/* Atajo para quien tría: desde la ficha del paciente se llega al
            triaje sin tener que entrar a la atención y buscar el botón. */}
        {c.pagada && !cerrada && ep == null && puedeTriar && c.triaje_requerido && (
          <Link to={`${base}/citas/${c.id}?triaje=1`} className={linkCls}>
            Tomar triaje →
          </Link>
        )}
        {!c.pagada && !cerrada && (
          <Link to={`${base}/caja?cita=${c.id}`} className={linkCls}>
            Cobrar en Caja →
          </Link>
        )}
        {!cerrada && c.episodio_id == null && (
          <button
            onClick={onCancel}
            className="text-xs font-medium text-[color:var(--color-danger)] hover:underline"
          >
            Cancelar cita
          </button>
        )}
      </div>
    </li>
  );
}


// ---------- Historia clínica del paciente ----------

// TODO: definido pero no se renderiza en ninguna parte. Conservado a la
// espera de decidir si se usa o se borra.
// @ts-expect-error TS6133 — sin usar a propósito, ver TODO de arriba
function HistoriaClinicaCard({ pacienteId, slug }: { pacienteId: number; slug: string }) {
  const base = useBase();
  const tz = useZonaHoraria();
  const { data: historia, isLoading } = useQuery({
    queryKey: ["historia", pacienteId],
    queryFn: () => getHistoriaDePaciente(pacienteId),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-[color:var(--color-link)]" />
              Historia clínica
            </h2>
            {historia && (
              <p className="text-xs text-slate-500 mt-0.5">
                <span className="font-mono">{historia.numero}</span> · abierta el{" "}
                {fechaDeInstante(historia.apertura, tz)}
              </p>
            )}
            {!historia && !isLoading && (
              <p className="text-xs text-slate-500 mt-0.5">
                Sin atenciones registradas. La historia clínica se crea con la primera atención.
              </p>
            )}
          </div>
          <Link
            to={`${base}/pacientes/${pacienteId}/atender`}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[color:var(--color-link)] hover:bg-[color:var(--color-link)] text-white shadow-sm focus:ring-[color:var(--color-link)] px-4 py-2 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Nueva atención
          </Link>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {isLoading && (
          <div className="px-6 py-6 text-center text-sm text-slate-400">Cargando…</div>
        )}
        {historia && historia.episodios.length === 0 && (
          <div className="px-6 py-8 text-center text-sm text-slate-500">
            Aún no hay episodios registrados en esta historia.
          </div>
        )}
        {historia && historia.episodios.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Fecha</th>
                  <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Profesional</th>
                  <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Especialidad</th>
                  <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Diagnóstico principal</th>
                  <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Estado</th>
                </tr>
              </thead>
              <tbody>
                {historia.episodios.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-6 py-3 text-xs whitespace-nowrap text-slate-700">
                      <Link
                        to={`${base}/episodios/${e.id}`}
                        className="text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium"
                      >
                        {fechaHora(e.inicio, tz)}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-slate-700">{e.profesional_nombre}</td>
                    <td className="px-6 py-3 text-slate-600">{e.especialidad_nombre ?? "—"}</td>
                    <td className="px-6 py-3">
                      {e.dx_principal ? (
                        <div>
                          <span className="font-mono text-xs text-slate-500 mr-1">
                            {e.dx_principal_codigo}
                          </span>
                          <span className="text-slate-900">{e.dx_principal}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {e.estado === "CERRADO" ? (
                        <Badge variant="success">
                          <FileSignature className="w-3 h-3 inline mr-0.5" />
                          Cerrado
                        </Badge>
                      ) : e.estado === "ABIERTO" ? (
                        <Badge variant="warning">Borrador</Badge>
                      ) : (
                        <Badge variant="danger">Anulado</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
